import { CapturePayload, Collection, CollectionPayload, SavedItem } from '../types'

interface MockDatabase {
  savedItems: SavedItem[]
  collections: Collection[]
}

const latency = async (ms = 250) => new Promise((resolve) => setTimeout(resolve, ms))

const nowIso = () => new Date().toISOString()

const createInitialData = (): MockDatabase => {
  const collections: Collection[] = [
    {
      id: 'col-weekend',
      name: 'Weekend deep dives',
      description: 'Long-form analysis to read on Saturdays',
      createdAt: nowIso(),
      updatedAt: nowIso(),
      color: '#9b59b6',
      isPinned: true,
      itemCount: 2,
    },
    {
      id: 'col-ai',
      name: 'AI strategy',
      description: 'Keep up with fast-moving AI landscape',
      createdAt: nowIso(),
      updatedAt: nowIso(),
      color: '#1b7f5f',
      isPinned: false,
      itemCount: 1,
    },
  ]

  const savedItems: SavedItem[] = [
    {
      id: 'item-1',
      url: 'https://openai.com/index/new-model-announcement',
      title: 'New multimodal models for everyday research workflows',
      author: 'OpenAI Research',
      imageUrl: undefined,
      savedAt: nowIso(),
      updatedAt: nowIso(),
      status: 'ready',
      note: 'Assess whether summarisation quality improves for niche topics.',
      tags: ['ai', 'research'],
      collectionIds: ['col-ai'],
      summary: {
        shortSummary:
          'OpenAI outlines an upgraded multimodal family with faster context windows, better reasoning, and developer-accessible APIs.',
        keyPoints: [
          'Adds low-latency GPT-4o-mini tier for bulk summarisation jobs',
          'Enhances document ingestion with structured output tools',
          'Launches responsible-use guardrails and auditing hooks',
        ],
        generatedAt: nowIso(),
        model: 'mock-gpt-4o-mini',
      },
      errorMessage: undefined,
    },
    {
      id: 'item-2',
      url: 'https://stratechery.com/podcast/the-end-of-read-it-later/',
      title: 'Thoughts on the end of read-it-later apps',
      author: 'Ben Thompson',
      imageUrl: undefined,
      savedAt: nowIso(),
      updatedAt: nowIso(),
      status: 'ready',
      note: undefined,
      tags: ['productivity'],
      collectionIds: ['col-weekend'],
      summary: {
        shortSummary: 'Explores why bookmarking tools faded and what modern alternatives must deliver to stay relevant.',
        generatedAt: nowIso(),
      },
      errorMessage: undefined,
    },
    {
      id: 'item-3',
      url: 'https://techreview.com/article/rethinking-daily-news-digests/',
      title: 'Rethinking daily news digests in 2025',
      author: undefined,
      imageUrl: undefined,
      savedAt: nowIso(),
      updatedAt: nowIso(),
      status: 'summarizing',
      note: 'Follow up once summarisation pipeline lands.',
      tags: ['news'],
      collectionIds: ['col-weekend'],
      summary: undefined,
      errorMessage: undefined,
    },
  ]

  return { savedItems, collections }
}

let mockDb: MockDatabase | null = null
let originalFetch: typeof fetch | null = null

const parseJson = async (request: Request) => {
  if (request.headers.get('content-type')?.includes('application/json')) {
    return request.json()
  }
  if (request.headers.get('content-type')?.includes('form')) {
    const form = await request.formData()
    const result: Record<string, unknown> = {}
    form.forEach((value, key) => {
      result[key] = value
    })
    return result
  }
  return undefined
}

const matchesPath = (pathname: string, target: string) => pathname === target || pathname === `${target}/`

const sendJson = (body: unknown, init: ResponseInit = {}) =>
  new Response(JSON.stringify(body), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })

const notFound = () => new Response(JSON.stringify({ message: 'Not found' }), { status: 404 })

const badRequest = (message: string) => new Response(JSON.stringify({ message }), { status: 400 })

const delayAndRespond = async (body: unknown, init?: ResponseInit) => {
  await latency()
  return sendJson(body, init)
}

const createSavedItemFromPayload = (payload: CapturePayload): SavedItem => {
  const id = crypto.randomUUID()
  return {
    id,
    url: payload.url,
    title: payload.title,
    author: undefined,
    imageUrl: undefined,
    savedAt: nowIso(),
    updatedAt: nowIso(),
    status: 'summarizing',
    note: payload.note,
    tags: payload.tags ?? [],
    collectionIds: payload.collectionIds ?? [],
    summary: {
      shortSummary: 'Summarisation queued — this mock environment simulates background processing.',
      generatedAt: nowIso(),
      model: 'mock-gpt-4o-mini',
    },
    errorMessage: undefined,
  }
}

const updateCollectionCounts = (db: MockDatabase) => {
  db.collections = db.collections.map((collection) => ({
    ...collection,
    itemCount: db.savedItems.filter((item) => item.collectionIds.includes(collection.id)).length,
    updatedAt: nowIso(),
  }))
}

const handleSavedItems = async (request: Request, pathname: string, db: MockDatabase) => {
  const url = new URL(request.url)
  const method = request.method.toUpperCase()

  if (matchesPath(pathname, '/api/urls') && method === 'GET') {
    const page = Number(url.searchParams.get('page') ?? '1')
    const pageSize = Number(url.searchParams.get('pageSize') ?? '20')
    const search = url.searchParams.get('search')?.toLowerCase() ?? ''
    const status = url.searchParams.get('status') ?? ''

    let filtered = [...db.savedItems]

    if (search) {
      filtered = filtered.filter((item) => {
        const haystack = [item.title, item.url, item.note, item.tags.join(' ')].join(' ').toLowerCase()
        return haystack.includes(search)
      })
    }

    if (status) {
      filtered = filtered.filter((item) => item.status === status)
    }

    const total = filtered.length
    const start = Math.max(0, (page - 1) * pageSize)
    const slice = filtered.slice(start, start + pageSize)

    return delayAndRespond({
      data: slice,
      pagination: {
        page,
        pageSize,
        total,
      },
    })
  }

  if (matchesPath(pathname, '/api/saved-items') && method === 'POST') {
    const payload = (await parseJson(request)) as CapturePayload | undefined
    if (!payload?.url) {
      return badRequest('A valid URL is required to save an item.')
    }

    const newItem = createSavedItemFromPayload(payload)
    db.savedItems.unshift(newItem)
    updateCollectionCounts(db)
    return delayAndRespond(newItem, { status: 201 })
  }

  const detailMatch = pathname.match(/\/api\/saved-items\/([^/]+)/)
  if (detailMatch) {
    const id = decodeURIComponent(detailMatch[1])
    const existingIndex = db.savedItems.findIndex((item) => item.id === id)

    if (existingIndex === -1) {
      return notFound()
    }

    if (method === 'GET') {
      return delayAndRespond(db.savedItems[existingIndex])
    }

    if (method === 'PATCH') {
      const updates = (await parseJson(request)) as Partial<CapturePayload> | undefined
      const updated: SavedItem = {
        ...db.savedItems[existingIndex],
        ...updates,
        updatedAt: nowIso(),
        tags: updates?.tags ?? db.savedItems[existingIndex].tags,
        collectionIds: updates?.collectionIds ?? db.savedItems[existingIndex].collectionIds,
      }
      db.savedItems[existingIndex] = updated
      updateCollectionCounts(db)
      return delayAndRespond(updated)
    }

    if (method === 'DELETE') {
      db.savedItems.splice(existingIndex, 1)
      updateCollectionCounts(db)
      return delayAndRespond({}, { status: 204 })
    }

    if (method === 'POST' && pathname.endsWith('/resummarize')) {
      const item = db.savedItems[existingIndex]
      item.status = 'summarizing'
      item.updatedAt = nowIso()
      item.summary = {
        shortSummary: 'Generating an updated summary…',
        generatedAt: nowIso(),
        model: 'mock-gpt-4o-mini',
      }

      setTimeout(() => {
        item.status = 'ready'
        item.updatedAt = nowIso()
        item.summary = {
          shortSummary: 'Mock summary refreshed. Connect the real AI pipeline to replace this copy.',
          generatedAt: nowIso(),
          model: 'mock-gpt-4o-full',
        }
      }, 1200)

      return delayAndRespond(item)
    }
  }

  return null
}

const handleCollections = async (request: Request, pathname: string, db: MockDatabase) => {
  const method = request.method.toUpperCase()

  if (matchesPath(pathname, '/api/collections') && method === 'GET') {
    return delayAndRespond(db.collections)
  }

  if (matchesPath(pathname, '/api/collections') && method === 'POST') {
    const payload = (await parseJson(request)) as CollectionPayload | undefined
    if (!payload?.name) {
      return badRequest('Collection name is required.')
    }

    const now = nowIso()
    const newCollection: Collection = {
      id: crypto.randomUUID(),
      name: payload.name,
      description: payload.description,
      color: payload.color,
      isPinned: false,
      createdAt: now,
      updatedAt: now,
      itemCount: 0,
    }

    db.collections.unshift(newCollection)
    return delayAndRespond(newCollection, { status: 201 })
  }

  const detailMatch = pathname.match(/\/api\/collections\/([^/]+)/)
  if (detailMatch) {
    const id = decodeURIComponent(detailMatch[1])
    const idx = db.collections.findIndex((collection) => collection.id === id)

    if (idx === -1) {
      return notFound()
    }

    if (method === 'GET') {
      return delayAndRespond(db.collections[idx])
    }

    if (method === 'PATCH') {
      const updates = (await parseJson(request)) as Partial<CollectionPayload> | undefined
      const updated: Collection = {
        ...db.collections[idx],
        ...updates,
        updatedAt: nowIso(),
      }
      db.collections[idx] = updated
      return delayAndRespond(updated)
    }

    if (method === 'DELETE') {
      db.collections.splice(idx, 1)
      db.savedItems = db.savedItems.map((item) => ({
        ...item,
        collectionIds: item.collectionIds.filter((collectionId) => collectionId !== id),
      }))
      updateCollectionCounts(db)
      return delayAndRespond({}, { status: 204 })
    }
  }

  return null
}

export const initializeMockApi = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (import.meta.env.VITE_USE_MOCK_API !== 'true') {
    return
  }

  if (originalFetch) {
    return
  }

  mockDb = createInitialData()
  originalFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const request = new Request(input, init)
    const { pathname } = new URL(request.url, window.location.origin)

    if (!pathname.startsWith('/api/')) {
      return originalFetch!(input, init)
    }

    const handlers = [handleSavedItems, handleCollections]

    for (const handler of handlers) {
      const response = await handler(request, pathname, mockDb!)
      if (response) {
        return response
      }
    }

    return notFound()
  }
}

export const teardownMockApi = () => {
  if (originalFetch) {
    window.fetch = originalFetch
    originalFetch = null
    mockDb = null
  }
}
